import {
  FileAttachmentInput,
  InMailMessageOutput,
  useGetInMailUsageQuery,
  useMessageDetailQuery,
  useSendMessageMutation,
} from 'graphql/generated/hasura';
import { useCallback, useMemo } from 'react';
import moment from 'moment';
import { STANDARD_DATE } from 'utilities/dateHelper';
import { useAuth } from 'auth/context/AuthContext';
import { Roles } from '../../../firebase/interfaces';
import { NetworkStatus } from '@apollo/client';

type GroupedMessages = {
  [key: string]: InMailMessageOutput[];
};

export const MAX_PATIENT_MESSAGES = 25;

const groupByDate = (messages: InMailMessageOutput[]) => {
  return messages.reduce<GroupedMessages>(
    (acc, { date, ...restMessageData }) => {
      const day = moment(date).utc().startOf('day').format(STANDARD_DATE);
      if (!acc?.[day]) {
        acc[day] = [];
      }
      acc[day].push({ date, ...restMessageData });
      return acc;
    },
    {},
  );
};

export const useGetConversation = (conversationId: string) => {
  const { user } = useAuth();

  const { data, loading, refetch, fetchMore, networkStatus } =
    useMessageDetailQuery({
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip: !conversationId,
      variables: {
        filter: { contactCodexId: conversationId },
      },
    });

  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;

  const { data: messagesCount } = useGetInMailUsageQuery();
  const used = useMemo(
    () => messagesCount?.getInMailUsage.used ?? 0,
    [messagesCount],
  );

  const [handleSend, { loading: disabledSend }] = useSendMessageMutation({
    refetchQueries: [
      'GetProviderInMails',
      'GetProviderInMailsCounts',
      'GetInMailUsage',
    ],
    onCompleted: () => {
      refetch();
    },
  });

  const onSend = (text: string, attachments?: FileAttachmentInput) => {
    return handleSend({
      variables: {
        inMailId: conversationId || '',
        text: text,
        attachments,
      },
    });
  };

  const conversation = useMemo(() => data?.getInMailDetail, [data]);
  const messages = useMemo(
    () => Object.entries(groupByDate(data?.getInMailDetail?.messages ?? [])),
    [data],
  );

  const handleLoadMore = useCallback(() => {
    const nextPageToken = data?.getInMailDetail?.nextPageToken;

    if (loading || !nextPageToken) return;

    return fetchMore({
      variables: {
        pageToken: nextPageToken,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        const messages = [
          ...(prev.getInMailDetail?.messages ?? []),
          ...(fetchMoreResult.getInMailDetail?.messages ?? []),
        ];
        return {
          getInMailDetail: {
            ...prev.getInMailDetail,
            nextPageToken: fetchMoreResult.getInMailDetail.nextPageToken,
            messages,
          },
        };
      },
    });
  }, [data?.getInMailDetail?.nextPageToken, loading, fetchMore]);

  const isMaxMessages = useMemo(
    () => (user?.role === Roles.PATIENT ? used >= MAX_PATIENT_MESSAGES : false),
    [used, user?.role],
  );

  return {
    conversation,
    messages,
    onSend,
    handleComplete: () => ({}),
    disabledSend,
    loading,
    isMaxMessages,
    messagesCount: Math.max(0, used),
    handleLoadMore,
    isFetchingMore,
  };
};
